/**
 * The regulatory panel (13-10): reportability, SIRS, serious-injury and restrictive-practice calls,
 * built to design/mock-13's `dialog.sheetpanel` (D-13-02-1). One-tap agree must never be the way a
 * reportable call gets made (D-13-00-5, CONTEXT), so:
 *
 *   - the full context comes first: the row's description, where it came from, the parent
 *     Incident's own key fields (read only, `loadIncident`), and the row's options as written
 *     (collapsed, because that text carries the agent's view);
 *   - the choices are neutral radio buttons in the order written: none selected, no recommended
 *     styling, no ordering hint;
 *   - a reason of at least 20 characters is required, and Decide (charcoal) stays disabled until a
 *     choice and the reason are both there; the footer says it is recorded under your name;
 *   - if the Incident's `ReportabilityDecision` disagrees with the choice, a non-blocking note says
 *     so. The panel never writes the Incident (cross-register writes are out of scope; CARD-QS-001
 *     step 4 is the human path).
 *
 * Oversight decides; anyone else opens it read-only ("View context"). React Aria's `ModalOverlay` /
 * `Modal` / `Dialog` give the focus trap and restore, Escape and scroll lock (RESEARCH "Don't
 * hand-roll"). Escape and Cancel close it without deciding, and focus goes back to the button
 * that opened it. The body is keyed per opening, so every opening starts with nothing chosen.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { isRegulatoryDecision } from '../../lib/actionAuthority';
import { decidedOptionValue, DECIDED_OPTION_MAX, incidentDisagreement, optionText, parseOptions, reportabilityWords } from '../../lib/options';
import { regulatoryBecauseText } from '../../lib/todayQueue';
import { dueLabel } from '../../lib/briefing';
import type { IncidentContext } from '../../services/actionService';
import type { ActionRow } from '../../types/actionRegister';
import styles from './today.module.css';

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');

/** The shortest reason the panel accepts (13-10 plan). */
export const MIN_REASON = 20;

export interface RegulatoryDecision {
  /** The `DecidedOption` value (255 at most). */
  option: string;
  /** The choice in words, for the toast. */
  label: string;
  reason: string;
  /** The "incident record says otherwise" note, when there is one. */
  disagreement: string | null;
}

export interface RegulatoryPanelProps {
  /** The row (kept while the panel closes, so focus returns to the button that opened it). */
  row: ActionRow | null;
  isOpen: boolean;
  readOnly: boolean;
  /** Changes on every opening, so each one starts with nothing chosen. */
  opening: number;
  now: number;
  loadIncident?: (parentId: string) => Promise<IncidentContext>;
  onClose: () => void;
  /** Records the decision. A refusal comes back as words, shown in the panel, which stays open. */
  onDecide: (decision: RegulatoryDecision) => string | null;
}

type IncidentState =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'ready'; incident: IncidentContext }
  | { status: 'error'; message: string };

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

function sourceText(row: ActionRow): string {
  if (!row.sourceRef) return row.source;
  return row.source === 'Other' ? row.sourceRef : `${row.source} ${row.sourceRef}`;
}

export default function RegulatoryPanel({ row, isOpen, readOnly, opening, now, loadIncident, onClose, onDecide }: RegulatoryPanelProps) {
  return (
    <ModalOverlay
      className={styles.overlay}
      isOpen={isOpen && row !== null}
      isDismissable
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <Modal className={styles.sheetpanel}>
        <Dialog className={styles.dialog} aria-label={row ? row.title : 'Regulatory decision'}>
          {row && <PanelBody key={opening} row={row} readOnly={readOnly} now={now} loadIncident={loadIncident} onDecide={onDecide} />}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function PanelBody({
  row,
  readOnly,
  now,
  loadIncident,
  onDecide,
}: Omit<RegulatoryPanelProps, 'row' | 'isOpen' | 'onClose' | 'opening'> & { row: ActionRow }) {
  const parsed = useMemo(() => parseOptions(row.options), [row.options]);
  const free = parsed.options.length === 0;
  const [choice, setChoice] = useState<string | null>(null); // nothing chosen for you
  const [own, setOwn] = useState('');
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);

  const isIncident = row.parentType === 'Incident' && !!row.parentId;
  const [incident, setIncident] = useState<IncidentState>(() =>
    isIncident && loadIncident ? { status: 'loading' } : { status: 'none' },
  );
  useEffect(() => {
    if (!isIncident || !loadIncident || !row.parentId) return;
    let live = true;
    loadIncident(row.parentId)
      .then((inc) => live && setIncident({ status: 'ready', incident: inc }))
      .catch((err: unknown) =>
        live && setIncident({ status: 'error', message: err instanceof Error ? err.message : 'It could not be read.' }),
      );
    return () => {
      live = false;
    };
  }, [isIncident, loadIncident, row.parentId]);

  const chosen = free ? null : parsed.options.find((o) => o.key === choice) ?? null;
  const label = free ? own.trim() : chosen?.label ?? '';
  const reasonLeft = Math.max(0, MIN_REASON - reason.trim().length);
  const ready = !!label && reasonLeft === 0;
  const disagreement =
    label && incident.status === 'ready' ? incidentDisagreement(label, incident.incident.reportabilityDecision) : null;

  const hint = !label && reasonLeft > 0
    ? 'Choose an option and write your reason to decide.'
    : !label
      ? free
        ? 'Write your decision.'
        : 'Choose an option.'
      : reasonLeft > 0
        ? `Write your reason to decide (${reasonLeft} more character${reasonLeft === 1 ? '' : 's'}).`
        : 'This records a regulatory decision under your name.';

  const decide = () => {
    if (!ready) return;
    const option = free ? decidedOptionValue(label) : chosen ? decidedOptionValue(chosen) : '';
    setRefusal(onDecide({ option, label, reason: reason.trim(), disagreement }));
  };

  const due = dueLabel(row, new Date(now));
  const because = regulatoryBecauseText(isRegulatoryDecision(row).because);

  return (
    <>
      <div className={styles.ph}>
        <div>
          <Heading slot="title" className={styles.phTitle}>
            {row.title}
          </Heading>
          <div className={styles.phSub}>
            {row.reference}, from {sourceText(row)}
            {due?.text ? `. ${due.text}` : ''}
          </div>
        </div>
        <Button slot="close" className={styles.xbtn} aria-label="Close">
          <XIcon />
        </Button>
      </div>

      <div className={styles.pb}>
        {row.description?.trim() && (
          <>
            <h3 className={styles.pbH}>What&rsquo;s being decided</h3>
            <p className={styles.pbP}>{row.description}</p>
          </>
        )}

        {isIncident && (
          <>
            <h3 className={styles.pbH}>The incident record</h3>
            {incident.status === 'loading' && <p className={styles.pbMuted}>Reading {row.parentId}&hellip;</p>}
            {incident.status === 'none' && <p className={styles.pbMuted}>{row.parentId} can&rsquo;t be shown here. Open it on the Incidents register.</p>}
            {incident.status === 'error' && (
              <p className={styles.pbMuted} role="status">
                {row.parentId} couldn&rsquo;t be read ({incident.message}). You can still decide; check the incident on its register.
              </p>
            )}
            {incident.status === 'ready' && (
              <dl className={styles.facts}>
                <dt>Incident</dt>
                <dd>
                  {incident.incident.reference}
                  {incident.incident.title ? `: ${incident.incident.title}` : ''}
                </dd>
                <dt>Category</dt>
                <dd>{incident.incident.category ?? 'Not recorded'}</dd>
                <dt>Severity</dt>
                <dd>{incident.incident.severity ?? 'Not recorded'}</dd>
                <dt>Reportability decision</dt>
                <dd>{reportabilityWords(incident.incident.reportabilityDecision)}</dd>
                <dt>Confidence</dt>
                <dd>{incident.incident.confidence === null ? 'Not recorded' : `${incident.incident.confidence}%`}</dd>
                <dt>Assessment</dt>
                <dd className={styles.pre}>{incident.incident.reportabilityAssessment ?? 'Not recorded'}</dd>
              </dl>
            )}
          </>
        )}

        <h3 className={styles.pbH}>Where it came from</h3>
        <dl className={styles.facts}>
          <dt>Source</dt>
          <dd>{sourceText(row)}</dd>
          {row.parentId && row.parentId !== row.sourceRef && (
            <>
              <dt>Parent</dt>
              <dd>
                {row.parentType ? `${row.parentType} ` : ''}
                {row.parentId}
              </dd>
            </>
          )}
          <dt>Why it&rsquo;s here</dt>
          <dd>{because}</dd>
        </dl>

        {row.options?.trim() && !free && (
          <details className={styles.raw}>
            <summary>The options as written on the row</summary>
            <p className={styles.pre}>{row.options}</p>
            <p className={styles.pbMuted}>This wording may include the agent&rsquo;s view. The choices below are listed without it.</p>
          </details>
        )}

        {readOnly ? (
          <>
            <h3 className={styles.pbH}>The options</h3>
            {free ? (
              <p className={cx(styles.pbP, styles.pre)}>{row.options?.trim() || 'No options recorded on this row.'}</p>
            ) : (
              <div className={styles.opts}>
                {parsed.options.map((o) => (
                  <div key={o.key} className={cx(styles.optRow, styles.optRo)}>
                    <b>{optionText(o.label)}</b>
                  </div>
                ))}
              </div>
            )}
            <p className={styles.roNote}>Oversight makes this call. You&rsquo;ll see the outcome here once it&rsquo;s recorded.</p>
          </>
        ) : (
          <>
            {free ? (
              <>
                <h3 className={styles.pbH}>
                  <label htmlFor="reg-own">Your decision</label>
                </h3>
                {row.options?.trim() && <p className={cx(styles.pbP, styles.pre)}>{row.options}</p>}
                <input
                  id="reg-own"
                  className={styles.input}
                  style={{ width: '100%' }}
                  value={own}
                  maxLength={DECIDED_OPTION_MAX}
                  onChange={(e) => setOwn(e.target.value)}
                  placeholder="Required. The decision in one line."
                  autoComplete="off"
                />
              </>
            ) : (
              <>
                <h3 className={styles.pbH}>
                  Your decision
                </h3>
                <fieldset className={styles.opts}>
                  <legend className={styles.legend}>The options are listed as written. Nothing is chosen for you.</legend>
                  {parsed.options.map((o) => (
                    <label key={o.key} className={styles.optRow}>
                      <input
                        type="radio"
                        name={`reg-${row.id}`}
                        value={o.key}
                        checked={choice === o.key}
                        onChange={() => setChoice(o.key)}
                      />
                      <b>{optionText(o.label)}</b>
                    </label>
                  ))}
                </fieldset>
              </>
            )}

            {disagreement && (
              <p className={styles.note} role="status" style={{ marginTop: 14 }}>
                {disagreement}
              </p>
            )}

            <h3 className={styles.pbH}>
              <label htmlFor="reg-reason">Your reason</label>
            </h3>
            <textarea
              id="reg-reason"
              className={cx(styles.input, styles.textarea)}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required. Write it as you would explain it to the regulator."
              aria-describedby="reg-hint"
            />
            <p className={styles.pbMuted}>It is saved with the decision, in the audit trail and the row&rsquo;s evidence.</p>
            {refusal && (
              <p className={styles.note} role="alert" style={{ marginTop: 12 }}>
                {refusal}
              </p>
            )}
          </>
        )}
      </div>

      <div className={styles.pf}>
        {readOnly ? (
          <>
            <span className={styles.pfHint}>Read only for your role.</span>
            <Button slot="close" className={cx(styles.btn, styles.line)}>
              Close
            </Button>
          </>
        ) : (
          <>
            <span className={styles.pfHint} id="reg-hint" aria-live="polite">
              {hint}
            </span>
            <Button slot="close" className={cx(styles.btn, styles.quiet)}>
              Cancel
            </Button>
            <button type="button" className={cx(styles.btn, styles.solemn)} disabled={!ready} onClick={decide}>
              Decide
            </button>
          </>
        )}
      </div>
    </>
  );
}
